import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Home,
  Users,
  Clock,
  FileText,
  Settings,
  Briefcase,
  Bell,
  LogOut,
  BarChart2,
  Grid,
  Activity,
  Folder,
  Monitor,
  Building,
  Package,
  FlaskConical,
  UserCheck,
  Search,
  ChevronDown,
  ChevronRight,
  Map,
  MapPin,
  Layers,
  ClipboardPen,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";

const SidebarResponsive = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({
    analytics: true,
    management: true,
    hr: true,
    system: true,
  });
  const [expandedSubmenus, setExpandedSubmenus] = useState({});
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user"))
  );

  const [touchStart, setTouchStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleStorage = () => {
      setUser(JSON.parse(localStorage.getItem("user")));
    };
    window.addEventListener("storage", handleStorage);
    handleStorage();
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const isMonitoring = location.pathname.startsWith("/monitoring");
  const isAdmin = user?.role === "admin";
  const isAdminProduk = user?.role === "admin-produk";
  const isAdminProyek = user?.role === "admin-proyek";
  const isSupervisor = user?.role === "supervisor";
  const hasLimitedMenu = isAdminProduk || isAdminProyek;

  const getMenuGroups = () => {
    if (isMonitoring) {
      return {
        analytics: {
          label: "Analytics",
          items: [
            { name: "Overview", icon: BarChart2, path: "/monitoring/overview" },
            {
              name: "Performance",
              icon: Activity,
              path: "/monitoring/performance",
            },
            { name: "Reports", icon: FileText, path: "/monitoring/reports" },
          ],
        },
        system: {
          label: "System",
          items: [
            { name: "Modules", icon: Grid, path: "/monitoring/module" },
            { name: "Alerts", icon: Bell, path: "/monitoring/alerts" },
            { name: "Logs", icon: Folder, path: "/monitoring/logs" },
          ],
        },
      };
    }

    if (isSupervisor) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [
            {
              name: "Projects",
              icon: Briefcase,
              path: "/admin/projects",
              hasSubmenu: true,
              submenu: [
                {
                  name: "Project List",
                  icon: Briefcase,
                  path: "/admin/projects",
                },
                { name: "Project Map", icon: Map, path: "/admin/projects/map" },
                {
                  name: "Excavation Points",
                  icon: MapPin,
                  path: "/admin/excavations",
                },
              ],
            },
          ],
        },
      };
    }

    if (isAdminProduk) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [{ name: "Products", icon: Package, path: "/admin/products" }],
        },
      };
    }

    if (isAdminProyek) {
      return {
        analytics: {
          label: "Analytics",
          items: [{ name: "Dashboard", icon: Home, path: "/admin/dashboard" }],
        },
        management: {
          label: "Management",
          items: [
            {
              name: "Projects",
              icon: Briefcase,
              path: "/admin/projects",
              hasSubmenu: true,
              submenu: [
                {
                  name: "Project List",
                  icon: Briefcase,
                  path: "/admin/projects",
                },
                { name: "Project Map", icon: Map, path: "/admin/projects/map" },
                {
                  name: "Excavation Points",
                  icon: MapPin,
                  path: "/admin/excavations",
                },
              ],
            },
            { name: "Reports", icon: FileText, path: "/admin/reports" },
          ],
        },
      };
    }

    return {
      analytics: {
        label: "Analytics",
        items: [
          { name: "Dashboard", icon: Home, path: "/admin/dashboard" },
          { name: "Reports", icon: FileText, path: "/admin/reports" },
        ],
      },
      management: {
        label: "Management",
        items: [
          {
            name: "Projects",
            icon: Briefcase,
            path: "/admin/projects",
            hasSubmenu: true,
            submenu: [
              {
                name: "Project List",
                icon: ClipboardPen,
                path: "/admin/projects",
              },
              { name: "Project Map", icon: Map, path: "/admin/projects/map" },
              {
                name: "Excavation Points",
                icon: MapPin,
                path: "/admin/projects/excavation/map",
              },
              {
                name: "Map Overview",
                icon: Layers,
                path: "/admin/projects/map-overview",
              },
            ],
          },
          { name: "Products", icon: Package, path: "/admin/products" },
          { name: "Users", icon: Users, path: "/admin/users" },
          { name: "Clients", icon: UserCheck, path: "/admin/client" },
        ],
      },
      hr: {
        label: "Human Resources",
        items: [
          { name: "Employees", icon: Users, path: "/admin/employees" },
          { name: "Attendance", icon: Clock, path: "/admin/attendance" },
        ],
      },
    };
  };

  const menuGroups = getMenuGroups();

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const toggleSubmenu = (itemName) => {
    setExpandedSubmenus((prev) => ({
      ...prev,
      [itemName.toLowerCase()]: !prev[itemName.toLowerCase()],
    }));
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const navbarStyles = {
    admin: {
      container: "bg-deep text-white",
      mobileMenu: "bg-deep text-white",
      activeItem: "bg-deep-light",
      borderColor: "border-deep-light",
      switchButton: "bg-primary",
      groupHeader: "text-white/80",
      hoverItem: "hover:bg-deep-light",
    },
    monitoring: {
      container: "bg-white text-gray-700 border-b border-gray-200",
      mobileMenu: "bg-white text-gray-700",
      activeItem: "bg-primary-light/20 text-deep",
      borderColor: "border-gray-100",
      switchButton: "bg-deep",
      groupHeader: "text-gray-500",
      hoverItem: "hover:bg-gray-50",
    },
  };

  const styles = isMonitoring ? navbarStyles.monitoring : navbarStyles.admin;

  return (
    <>
      <nav
        className={`${styles.container} md:hidden fixed top-0 left-0 right-0 z-[60]`}
      >
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center">
            <img
              src="/images/logo.svg"
              alt="Logo"
              className="h-8 object-cover"
            />
          </Link>
          <h1 className="text-white font-semibold text-lg">Adhimaju Admin</h1>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-md"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <div
        className={`fixed top-16 bottom-0 right-0 w-64 z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        } ${styles.mobileMenu} overflow-y-auto`}
      >
        <div className="flex flex-col h-full">
          <div className="p-4">
            {/* {!hasLimitedMenu && (
              <div className="mb-4">
                <div
                  className={`flex items-center bg-opacity-20 ${
                    isMonitoring ? "bg-gray-200" : "bg-slate-800"
                  } rounded-xl px-3 py-2`}
                >
                  <Search size={16} className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search menu..."
                    className="bg-transparent border-none focus:outline-none text-sm ml-2 w-full"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            )} */}
          </div>

          <nav className="flex-1 px-4 overflow-y-auto">
            {Object.entries(menuGroups).map(([key, group]) => (
              <div key={key} className="mb-4">
                <div
                  className={`flex items-center justify-between px-4 py-2 cursor-pointer ${styles.groupHeader}`}
                  onClick={() => toggleGroup(key)}
                >
                  <span className="text-xs font-medium">{group.label}</span>
                  {expandedGroups[key] ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                </div>
                {expandedGroups[key] && (
                  <ul className="space-y-1">
                    {group.items.map((item) => {
                      const hasSubmenu = item.hasSubmenu;
                      const isSubmenuExpanded =
                        expandedSubmenus[item.name?.toLowerCase()];
                      const itemIsActive = hasSubmenu
                        ? item.submenu?.some((sub) => isActive(sub.path))
                        : isActive(item.path);

                      return (
                        <li key={item.name} className={`rounded-xl`}>
                          {hasSubmenu ? (
                            <>
                              <div
                                onClick={() => toggleSubmenu(item.name)}
                                className={`flex items-center justify-between px-3 py-2 text-sm rounded-xl transition-colors duration-300 ${
                                  itemIsActive
                                    ? styles.activeItem
                                    : styles.hoverItem
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <item.icon size={18} />
                                  <span>{item.name}</span>
                                </div>
                                {isSubmenuExpanded ? (
                                  <ChevronDown size={16} />
                                ) : (
                                  <ChevronRight size={16} />
                                )}
                              </div>
                              {isSubmenuExpanded && (
                                <ul className="mt-1 ml-6 space-y-1">
                                  {item.submenu?.map((subItem) => (
                                    <li key={subItem.path}>
                                      <Link
                                        to={subItem.path}
                                        className={`flex items-center gap-3 px-3 py-2 text-sm rounded-xl transition-colors duration-300 ${
                                          isActive(subItem.path)
                                            ? styles.activeItem
                                            : styles.hoverItem
                                        }`}
                                        onClick={() => setIsMenuOpen(false)}
                                      >
                                        <subItem.icon size={16} />
                                        <span>{subItem.name}</span>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </>
                          ) : (
                            <Link
                              to={item.path}
                              className={`flex items-center gap-3 px-3 py-2 text-sm rounded-xl transition-colors duration-300 ${
                                itemIsActive
                                  ? styles.activeItem
                                  : styles.hoverItem
                              }`}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <item.icon size={18} />
                              <span>{item.name}</span>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </nav>

          <div className={`border-t ${styles.borderColor} p-4`}>
            <button
              onClick={() => {
                localStorage.clear();
                navigate("/");
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-red-400 text-sm rounded-xl transition-colors duration-300"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SidebarResponsive;
